import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { MailrailAutoSendEmailCampaign, TPredicateMode } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CardV2 as Card,
  Select,
} from '@feathr/components';
import FilterChip from '@feathr/extender/components/EditFilters/FilterChip';
import Glue from '@feathr/extender/components/Glue';

import * as styles from './MailrailPredicatesConfig.css';

interface IProps {
  campaign: MailrailAutoSendEmailCampaign;
  disabled: boolean;
  label?: string;
}

interface IModeOption extends ISelectOption {
  id: TPredicateMode;
}

function MailrailPredicatesConfig({ campaign, disabled, label }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const predicates = campaign.get('predicates');
  const mode = campaign.get('predicates_mode') ?? 'match_any';
  const subtype = campaign.get('subtype');
  const predicateKind = subtype === 'time' ? 'attribute' : subtype;

  // Automatically add a predicate on mount if there are none
  useEffect(() => {
    if (!campaign.isPending && predicates.length === 0) {
      campaign.set({
        predicates: [
          {
            attr_against: undefined,
            kind: predicateKind,
          },
        ],
      });
    }
  }, [campaign.isPending]);

  const modeOptions: IModeOption[] = [
    { id: 'match_all', name: t('all') },
    { id: 'match_any', name: t('any') },
  ];

  const modeOption = modeOptions.find(({ id }) => id === mode) ?? modeOptions[0];

  function handleAddTrigger(): void {
    campaign.set({
      predicates: [
        ...predicates,
        {
          attr_against: undefined,
          kind: predicateKind,
        },
      ],
    });
  }

  function handleRemove(key: number): () => void {
    return function (): void {
      const newPredicates = predicates.filter((_, i) => i !== key);
      campaign.set({ predicates: newPredicates });
    };
  }

  function handleChangeMode(option: ISelectOption): void {
    const newMode = option.id as TPredicateMode;
    campaign.set({ predicates_mode: newMode, predicates: predicates });
  }

  const predicateInputs = (
    <>
      <div className={styles.header}>
        <Trans t={t}>
          Send an email to people that match{' '}
          <Select
            className={styles.mode}
            // Mode is limited to any when date triggered is selected
            disabled={campaign.get('subtype') === 'time'}
            name={'mailrail-predicates-mode'}
            onSelectSingle={handleChangeMode}
            options={modeOptions}
            value={modeOption}
          />
          of the following filters:
        </Trans>
      </div>
      <div>
        {predicates.map((predicate, index) => {
          const key = `${predicate.attr_against}${index}`;
          const lastItemIndex = predicates.length - 1;
          return (
            <div key={key}>
              {/* TODO: replace with <Predicates> in  #3595 */}
              <FilterChip
                disabled={disabled}
                filterContext={'pinpoint'}
                key={predicate.attr_against}
                model={campaign}
                onClickRemove={handleRemove(index)}
                predicate={predicate}
                triggers={predicates}
              />
              <Glue isLastItem={index === lastItemIndex} mode={mode} t={t} />
            </div>
          );
        })}
      </div>
    </>
  );

  const triggerActions = !disabled && (
    <Button
      name={'add_filter'}
      onClick={handleAddTrigger}
      prefix={<FontAwesomeIcon icon={faPlus} />}
    >
      {t('Trigger')}
    </Button>
  );

  return (
    <Card width={'full'}>
      <CardHeader title={label ?? t('Triggers')} />
      <CardContent>{predicateInputs}</CardContent>
      <CardActions contentClassName={styles.predicates}>{triggerActions}</CardActions>
    </Card>
  );
}

export default observer(MailrailPredicatesConfig);
